/**
 * Workspace atual.
 *
 * Quem resolve o `:slug` da URL para um workspace e um PAPEL é esta camada —
 * uma vez, no topo. Sem isso cada tela refaria a mesma pergunta e alguma delas
 * esqueceria o papel, que é o que decide o que aparece na tela.
 *
 * O papel vem do servidor dentro do workspace. O cliente nunca "escolhe" papel.
 */
import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { WorkspaceRole } from '@mgdlead/shared';

import { toWorkspaceRole } from '../../lib/api';
import type { WorkspaceSummary } from '../../lib/api';

export interface WorkspaceContextValue {
  workspace: WorkspaceSummary;
  role: WorkspaceRole;
  /** Todos os workspaces do usuário — alimenta o seletor da topbar. */
  workspaces: readonly WorkspaceSummary[];
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export interface WorkspaceProviderProps {
  workspace: WorkspaceSummary;
  workspaces: readonly WorkspaceSummary[];
  children: ReactNode;
}

export function WorkspaceProvider({ workspace, workspaces, children }: WorkspaceProviderProps) {
  const value = useMemo<WorkspaceContextValue>(
    () => ({ workspace, role: toWorkspaceRole(workspace.role), workspaces }),
    [workspace, workspaces],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace() precisa estar dentro de <WorkspaceProvider>.');
  }
  return context;
}
